import { Component, Input, OnInit, HostListener } from '@angular/core';
import { AuthService } from '../../../services/auth.service';

/**
 * WhatsApp Support Widget Component
 *
 * A floating chat widget that provides WhatsApp support with a modern chat interface.
 * Features a collapsible design with agent profile, welcome message, and direct WhatsApp integration.
 *
 * @example
 * Basic usage:
 * <whatsapp-support phoneNumber="447874475624"></whatsapp-support>
 *
 * @example
 * With custom agent and message:
 * <whatsapp-support
 *   phoneNumber="447874475624"
 *   agentName="Sarah"
 *   agentStatus="Usually replies within 2 hours"
 *   welcomeMessage="Hi! How can I help you today?"
 *   messageTemplate="Hello, my name is {userName}, I need help with {merchantName}">
 * </whatsapp-support>
 *
 * @example
 * With completely custom message:
 * <whatsapp-support
 *   phoneNumber="447874475624"
 *   customMessage="I need help with my account">
 * </whatsapp-support>
 */
@Component({
  selector: 'whatsapp-support',
  templateUrl: './whatsapp-support.component.html',
  styleUrls: ['./whatsapp-support.component.css'],
})
export class WhatsappSupportComponent implements OnInit {
  @Input() phoneNumber: string = '447874475624';
  @Input() displayText?: string;
  @Input() showIcon: boolean = true;
  @Input() customMessage?: string;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() agentName: string = 'Hannah';
  @Input() agentStatus: string = 'Typically replies within a day';
  @Input() welcomeMessage: string =
    "Hey there 👋\n\nI'm here to help, so let me know what's up and I'll be happy to find a solution!";
  @Input() messageTemplate?: string; // Custom template for the initial WhatsApp message

  isExpanded: boolean = false;
  isAnimating: boolean = false;
  isClosing: boolean = false;

  constructor(private authService: AuthService) {}

  ngOnInit(): void {
    if (!this.displayText) {
      this.displayText = `+${this.phoneNumber}`;
    }
  }

  toggleWidget(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.isAnimating) {
      return; // Prevent multiple rapid clicks during animation
    }

    if (this.isExpanded) {
      this.closeWidget();
    } else {
      this.isExpanded = true;
    }
  }

  private closeWidget(): void {
    this.isAnimating = true;
    this.isClosing = true;

    // Allow animation to complete before hiding
    setTimeout(() => {
      this.isExpanded = false;
      this.isAnimating = false;
      this.isClosing = false;
    }, 300); // Match the slideDown animation duration
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const widget = target.closest('.whatsapp-widget');

    // Close widget if clicking outside of it
    if (!widget && this.isExpanded && !this.isAnimating) {
      this.closeWidget();
    }
  }

  get whatsappUrl(): string {
    let url = `https://api.whatsapp.com/send?phone=${this.phoneNumber}`;

    // Create personalized message
    const message = this.getPersonalizedMessage();
    if (message) {
      url += `&text=${encodeURIComponent(message)}`;
    }

    return url;
  }

  private getPersonalizedMessage(): string {
    // Use custom message if provided
    if (this.customMessage) {
      return this.customMessage;
    }

    // Get user and merchant information
    const userName =
      this.authService.getUserProperty('name') ||
      this.authService.getUserProperty('firstName') ||
      'Customer';
    const merchantName =
      this.authService.getNestedUserProperty('merchant', 'name') ||
      'your business';

    // Use custom template if provided, otherwise use default
    if (this.messageTemplate) {
      return this.messageTemplate
        .replace('{userName}', userName)
        .replace('{merchantName}', merchantName);
    }

    // Default personalized message
    return `Hello, my name is ${userName}, I have a query relating to ${merchantName}.`;
  }

  get sizeClass(): string {
    return `whatsapp-${this.size}`;
  }

  get formattedWelcomeMessage(): string {
    return this.welcomeMessage.replace(/\n/g, '<br>');
  }

  get currentTime(): string {
    const now = new Date();
    return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
}
